import React, { useEffect, useState } from "react";
import { Hammer, Pencil, Loader2, Lock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCrypto } from "../context/CryptoContext";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const WorkshopBoard = ({ domain = "infrastructure" }) => {
  const { user } = useAuth();
  const { decryptNetText, encryptNetText, hasNetwork } = useCrypto();
  const isAdmin = user?.role === "admin";
  const [board, setBoard] = useState(undefined); // undefined = loading
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/boards/${domain}`);
      setBoard(data);
      if (!data.exists) { setText(""); return; }
      if (data.enc && data.ct) {
        const pt = await decryptNetText(data.ct, data.iv);
        setText(pt === null ? "__LOCKED__" : pt);
      } else {
        setText(data.text || "");
      }
    } catch {
      setBoard({ exists: false });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [domain]);

  const save = async () => {
    setSaving(true);
    let payload = { text: draft };
    if (draft.trim() && hasNetwork()) {
      const enc = await encryptNetText(draft);
      payload = { ct: enc.ct, iv: enc.iv, enc: true, text: "" };
    }
    try {
      await api.put(`/admin/boards/${domain}`, payload);
      setEditing(false);
      await load();
    } catch {
      alert("Could not save The Workshop. Please try again.");
    }
    setSaving(false);
  };

  if (board === undefined) return null; // loading, stay quiet
  const empty = !text || text === "";
  const locked = text === "__LOCKED__";
  // Members never see an empty board; only the founder gets the "add" prompt.
  if (empty && !isAdmin) return null;

  return (
    <div className="border border-[#14A89A]/30 bg-[#14A89A]/[0.05] p-5 mb-8" data-testid="workshop-board">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Hammer size={16} className="text-[#14A89A]" strokeWidth={1.75} />
          <span className="font-heading text-base text-white">The Workshop</span>
          <span className="font-body text-[9px] tracking-[0.2em] uppercase text-[#14A89A] border border-[#14A89A]/40 px-1.5 py-0.5">Pinned</span>
        </div>
        <div className="flex items-center gap-3">
          {board?.exists && board?.updated_at && (
            <span className="font-body text-[10px] text-zinc-500" data-testid="workshop-updated">Updated {timeAgo(board.updated_at)}</span>
          )}
          {isAdmin && !editing && (
            <button onClick={() => { setDraft(locked ? "" : text); setEditing(true); }} data-testid="workshop-edit-button" className="text-[#14A89A]/80 hover:text-[#14A89A] transition-colors" title="Update The Workshop">
              <Pencil size={15} />
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-3" data-testid="workshop-editor">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={"What I'm working on right now, and what's coming next..."}
            data-testid="workshop-textarea"
            rows={7}
            className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body resize-y leading-relaxed"
          />
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setEditing(false)} data-testid="workshop-cancel-button" className="font-body text-xs text-zinc-400 hover:text-white px-3 py-1.5">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="workshop-save-button" className="font-body text-xs bg-[#14A89A] text-[#0A0A0A] font-medium px-4 py-1.5 hover:bg-[#2BC4B5] transition-colors flex items-center gap-1.5 disabled:opacity-40">
              {saving && <Loader2 size={12} className="animate-spin" />} Save
            </button>
          </div>
        </div>
      ) : locked ? (
        <p className="font-body text-xs text-zinc-500 flex items-center gap-1.5"><Lock size={12} /> Encrypted - unlock to view</p>
      ) : empty ? (
        <button onClick={() => { setDraft(""); setEditing(true); }} data-testid="workshop-add-button" className="font-body text-sm text-[#14A89A] hover:text-[#2BC4B5] flex items-center gap-1.5 transition-colors">
          <Pencil size={14} /> Add your first update
        </button>
      ) : (
        <p className="font-body text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap" data-testid="workshop-text">{text}</p>
      )}
    </div>
  );
};
